import uid from 'easy-uid';
import { Circle, Infinite } from 'infinite-circle';
import EmbedEvents from '../EmbedEvents';
import EmbedScriptEvents from '../EmbedScriptEvents';
import scrollToElement from '../scrollToElement';
import EventEmitter from './EventEmitter';
import EventRouter from './EventRouter';
import ConfirmDialog from './confirmDialog/ConfirmDialog';

function getWindowViewportRect() {
	return {
		top: 0,
		left: 0,
		width: window.innerWidth,
		height: window.innerHeight
	};
}

const WRAPPER_STYLE = 'display: block;overflow: hidden;position: relative;';

const scrollCircle = new Circle({
	listen: notify => window.addEventListener('scroll', notify),
	unlisten: notify => window.removeEventListener('scroll', notify)
});

const infinite = new Infinite();
infinite.add(scrollCircle);

export default class SznDiscussion extends EventEmitter {
	constructor(service, element, config) {
		super();

		/**
		 * @protected
		 * @type {string}
		 */
		this._sentinel = uid();

		/**
		 * @protected
		 * @type {string}
		 */
		this._service = service;

		/**
		 * @protected
		 * @type {HTMLElement}
		 */
		this._wrapper = element;

		/**
		 * @protected
		 * @type {Config}
		 */
		this._config = config;

		/**
		 * @protected
		 * @type {boolean}
		 */
		this._contentReady = false;

		/**
		 * @protected
		 * @type {HTMLIFrameElement}
		 */
		this._iframe = this._createIframe();

		/**
		 * @protected
		 * @type {{ canonicalString: string, title: string, analyticsData: Object }}
		 */
		this._discusssionParams = null;

		/**
		 * @protected
		 * @type {ConfirmDialog}
		 */
		this._confirmDialog = null;

		EventRouter.register(this);

		this._wrapper.innerHTML = '';
		this._wrapper.style = WRAPPER_STYLE;
		this._wrapper.appendChild(this._iframe);

		this._instanceCircleId = this._registerScrollCircle();
	}

	get sentinel() {
		return this._sentinel;
	}

	handleDocumentResize() {
		this._updateBoundingRectInIframe(
			getWindowViewportRect(),
			this._getIframeBoundingClientRect()
		);
	}

	handleMessage(type, payload = {}) {
		switch (type) {
			case EmbedEvents.SENTINEL_HANDSHAKE_ACK:
				if (this._sentinel !== payload.sentinel) {
					return;
				}
				if (!this._contentReady) {
					this.fire('ready');
					this._contentReady = true;
				} else {
					this.fire('loaded');
				}
				break;

			case EmbedEvents.RESIZE:
				this._iframe.style.height = payload.height + 'px';

				this._updateBoundingRectInIframe(
					getWindowViewportRect(),
					this._getIframeBoundingClientRect()
				);

				this.fire('resized', { height: payload.height });
				break;

			case EmbedEvents.CONTENT_CHANGED:
				this.fire('contentChange', payload);
				break;

			case EmbedEvents.SCROLL:
				if (!this._config.allowScroll) {
					break;
				}

				this._scrollToPosition(payload);
				break;

			case EmbedEvents.LINK_REDIRECT:
				this._redirectLink(payload.url);
				break;

			case EmbedEvents.CONFIRM:
				this._showConfirmDialog(payload);
				break;

			case EmbedEvents.LOGIN:
				this.fire('login');
				break;

			case EmbedEvents.COOKIE_FIX:
				window.location.replace(
					`${this._config.embedUrl}/authenticate?returnUrl=${encodeURIComponent(
						window.location.href
					)}`
				);
				break;

			case EmbedEvents.ERROR:
				this.fire('error', { error: payload.error });
				break;
		}
	}

	loadDiscussion(canonicalString, metaData, analyticsData) {
		this._iframe.src = this._createIframeUrl(['discussion', canonicalString], {
			'discussion--metaData': metaData,
			'page--analyticsData': analyticsData
		});
	}

	loadThread(commentId, analyticsData) {
		this._iframe.src = this._createIframeUrl(['thread', commentId], {
			'page--analyticsData': analyticsData
		});
	}

	sendEvent(type, payload = {}) {
		if (!this._iframe || !this._iframe.contentWindow) {
			return;
		}

		this._iframe.contentWindow.postMessage(
			{
				sentinel: this._sentinel,
				type,
				payload
			},
			'*'
		);
	}

	unload() {
		scrollCircle.unregister(this._instanceCircleId);
		EventRouter.unregister(this);

		this._destroyIframe();
	}

	_createIframe() {
		const iframe = document.createElement('iframe');

		iframe.src = this._createIframeUrl();

		iframe.scrolling = 'no';
		iframe.style = 'display: block;position: relative;';

		return iframe;
	}

	/**
	 * Creates url to embedded website with given params.
	 *
	 * @param {Array} pathParams
	 * @param {Object<string, *>} queryStringParams
	 * @returns {string}
	 */
	_createIframeUrl(pathParams = [], queryStringParams = {}) {
		pathParams.unshift(this._service);
		queryStringParams['sentinel'] = this._sentinel;

		const path = pathParams.map(param => encodeURIComponent(param)).join('/');
		const queryString = Object.keys(queryStringParams)
			.filter(
				param =>
					typeof queryStringParams[param] !== 'undefined' &&
					queryStringParams[param] !== null
			)
			.map(param => {
				let value = queryStringParams[param];

				if (typeof value === 'object') {
					value = JSON.stringify(value);
				}

				return `${param}=${encodeURIComponent(value)}`;
			});

		return `${this._config.embedUrl}/${path}?${
			queryString.length ? queryString.join('&') : ''
		}`;
	}

	_destroyIframe() {
		if (this._wrapper) {
			this._wrapper.innerHTML = '';
			this._iframe = null;
		}
	}

	/**
	 * Returns bounding client rect of the instance iframe
	 *
	 * @returns {DOMRect}
	 */
	_getIframeBoundingClientRect() {
		const iframeClientRect = this._iframe.getBoundingClientRect();

		// MS Edge v18 fix
		// post message with payload type ClientRect results into error 80700019
		// we have to copy that into plain object
		return {
			bottom: iframeClientRect.bottom,
			height: iframeClientRect.height,
			left: iframeClientRect.left,
			right: iframeClientRect.right,
			top: iframeClientRect.top,
			width: iframeClientRect.width
		};
	}

	_redirectLink(url) {
		if (this._config.linksInNewTab) {
			window.open(url, '_blank');
		} else {
			window.location.assign(url);
		}
	}

	_registerScrollCircle() {
		let lastBoundingRect = '';

		return scrollCircle.register({
			read: () => ({
				window: getWindowViewportRect(),
				iframe: this._getIframeBoundingClientRect()
			}),
			write: ({ payload }) => {
				let boundingRectString;
				const { window, iframe } = payload;

				if ((boundingRectString = JSON.stringify(iframe)) === lastBoundingRect) {
					return;
				}

				this._updateBoundingRectInIframe(window, iframe);
				lastBoundingRect = boundingRectString;
			}
		});
	}

	_scrollToPosition({ scrollOffset, alignment, durationInMs, cancelOnUserScroll }) {
		const onCompleteCallback = () => {
			this.fire('scrolled');
			this.sendEvent(EmbedScriptEvents.SCROLL_DONE);
		};

		scrollToElement({
			element: this._iframe,
			alignment,
			additionalOffset: scrollOffset,
			durationInMs,
			cancelOnUserScroll,
			onCompleteCallback
		});
	}

	_showConfirmDialog({
		title,
		content,
		actionText,
		cancelText = 'Zrušit',
		skinColors = null
	}) {
		if (!this._confirmDialog) {
			this._confirmDialog = new ConfirmDialog(skinColors);

			const sendConfirmEvent = () => this.sendEvent(EmbedScriptEvents.CONFIRMED);
			const sendDeclineEvent = () => this.sendEvent(EmbedScriptEvents.DECLINED);

			this._confirmDialog.listen('confirm', sendConfirmEvent);
			this._confirmDialog.listen('decline', sendDeclineEvent);
		}

		this._confirmDialog.show({ title, content, actionText, cancelText });
	}

	/**
	 * Sends event with iframe bounding rect into the iframe
	 */
	_updateBoundingRectInIframe(window, iframe) {
		this.sendEvent(EmbedScriptEvents.BOUNDING_RECT_CHANGE, { window, iframe });
	}
}
